/**
 * ============================================================================
 * OBJECTS STORE - CORE STORE INITIALIZATION
 * ============================================================================
 * 
 * This file initializes the main objects store using the GenericStore pattern.
 * The store handles caching and syncing of library objects for games.
 * 
 * Architecture:
 * - Uses storeRegistry for persistent caching
 * - 30-minute TTL for cached data
 * - Supports optimistic updates for instant UI feedback
 * 
 * @module objects/store
 */

import { storeRegistry } from "@/rerun-manager/GenericStore";
import type { ObjectItem } from "./types";

// ============================================================================
// STORE CONFIGURATION
// ============================================================================

/** Cache time-to-live in milliseconds (30 minutes) */
const CACHE_TTL_MS = 1000 * 60 * 30;

/** Store key used for persistent caching */
const STORE_KEY = 'glossary_items'; // Kept for backward compatibility with existing cache

// ============================================================================
// STORE INSTANCE
// ============================================================================

/**
 * Main store instance for library objects.
 * 
 * Features:
 * - Persistent caching with configurable TTL
 * - Automatic sync from remote API
 * - Optimistic update support
 * 
 * @example
 * ```ts
 * // Get all cached data
 * const allObjects = objectsStore.getData();
 * 
 * // Update data
 * objectsStore.setData(newData);
 * 
 * // Sync from remote
 * await objectsStore.syncFromRemote(fetchFn);
 * 
 * // Clear cache
 * await objectsStore.clearCache();
 * ```
 */
export const objectsStore = storeRegistry.getStore<ObjectItem>({
  key: STORE_KEY,
  ttlMs: CACHE_TTL_MS,
});

// ============================================================================
// STORE UTILITIES
// ============================================================================

/**
 * Clear the objects cache.
 * Call this when you need to force a fresh fetch from the API.
 */
export async function clearObjectsCache(): Promise<void> {
  try {
    await objectsStore.clearCache();
    console.log('[ObjectsStore] Cache cleared successfully');
  } catch (error) {
    console.error('[ObjectsStore] Failed to clear cache:', error);
  }
}

/**
 * @deprecated Use clearObjectsCache instead
 */
export const clearGlossaryCache = clearObjectsCache;
